import {css} from 'aphrodite';
import React from 'react';

import {BenchlingExpressionType} from 'benchling-expressions/BenchlingExpressionTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';
import {
  sharedStyles,
  ConditionalLogicAllowedValuesBaseProps,
} from 'processes/modals/router-config/RouterConfigConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import PrimitiveTagsInput from 'ui-library/tags-input/PrimitiveTagsInput';

export interface ConditionalLogicValueListProps extends ConditionalLogicAllowedValuesBaseProps {
  // The scalar type of the left-hand lookup that each entered value is compared against.
  leafType: LeafType.TEXT | LeafType.INTEGER | LeafType.DECIMAL;
  dataTestAttrs?: DataTestAttrs;
}

const isNumericLeafType = (leafType: LeafType): boolean =>
  leafType === LeafType.INTEGER || leafType === LeafType.DECIMAL;

export default function ConditionalLogicValueList(props: ConditionalLogicValueListProps): JSX.Element | null {
  const isNumeric = isNumericLeafType(props.leafType);
  const itemType = props.constantExpression?.itemType ?? null;
  // A list constant is only compatible if it's unset or already a list whose element type exactly matches the
  // expected lookup type. Integer and decimal are treated as distinct (mirroring the strict type-mismatch
  // check in LogicalFunctionConfig).
  const isCompatible =
    itemType == null ||
    (itemType.name === ComplexTypeOperator.LIST &&
      (itemType.ofType == null || itemType.ofType === props.leafType));

  // Discriminate between integer and decimal input based on the expected lookup type: an integer field must
  // reject fractional values (e.g. "4.5"), while a decimal field accepts any finite number.
  const isValidNumericEntry = (query: string): boolean => {
    const numeric = Number(query);
    if (query.length === 0 || !Number.isFinite(numeric)) {
      return false;
    }
    return props.leafType === LeafType.INTEGER ? Number.isInteger(numeric) : true;
  };

  const constantValue = props.constantExpression?.value;
  const currentValues = Array.isArray(constantValue) ? constantValue.map((entry) => String(entry)) : [];

  const handleChange = (newStringValues: Array<string>): void => {
    const parsedValues = isNumeric ? newStringValues.map((entry) => Number(entry)) : newStringValues;
    const newParam = {
      name: props.expression?.parameters?.[1]?.name,
      expression: {
        type: BenchlingExpressionType.CONSTANT_VALUE,
        itemType: {
          name: ComplexTypeOperator.LIST as ComplexTypeOperator.LIST,
          ofType: isNumeric ? props.leafType : LeafType.TEXT,
        },
        // Leave the value undefined for an empty list so the condition reads as incomplete and cannot be saved.
        value: parsedValues.length > 0 ? parsedValues : undefined,
      },
    };
    const newExpression = {
      ...props.expression,
      parameters: [props.expression?.parameters?.[0], newParam],
    };
    props.setExpression(newExpression);
  };

  return (
    <PrimitiveTagsInput
      className={css(sharedStyles.routerInputControl)}
      value={currentValues}
      placeholder={isNumeric ? 'enter numbers' : 'enter values'}
      disabled={!isCompatible}
      shouldCreateItem={isNumeric ? isValidNumericEntry : undefined}
      onChange={handleChange}
      dataTestAttrs={props.dataTestAttrs ?? {component: 'ConditionalLogicValueList', element: 'root'}}
    />
  );
}
